/**
 * AnnotationCanvas — SVG overlay positioned over a single rendered PDF page.
 *
 * Items (stored in PDF user-space coordinates so they survive zoom):
 *   { id, tool: 'pen',         color, size, points: [[x,y,p?]] }
 *   { id, tool: 'highlighter', color, size, points: [[x,y]] }
 *   { id, tool: 'rect',        color, size, x, y, w, h }
 *   { id, tool: 'line',        color, size, x1, y1, x2, y2 }
 *   { id, tool: 'text',        color, fontSize, x, y, text }
 *
 * Tools dispatched by the `tool` prop:
 *   pen | highlighter | eraser | text | rect | line | select
 *
 * `select` (lasso) lets you draw a free-form region; everything inside becomes
 * selected. Click an existing item with `select` to single-select; drag a
 * selected item to translate the whole selection. Backspace deletes selection.
 *
 * Undo/redo is owned by the canvas — every mutation pushes onto a 50-deep
 * history stack. There's a small floating ↶ ↷ in the corner of each page.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getStroke } from 'perfect-freehand';

const HIGHLIGHTER_OPACITY = 0.35;
const HISTORY_LIMIT = 50;
const SELECT_HANDLE_PAD = 6;

// ────────────────────────────────────────────────────────────────────────
// Path / geometry helpers
// ────────────────────────────────────────────────────────────────────────
function penPath(item) {
  const outline = getStroke(item.points, {
    size: item.size, thinning: 0.5, smoothing: 0.5, streamline: 0.4,
    simulatePressure: !item.points[0]?.[2],
  });
  if (outline.length < 3) return '';
  return outline.reduce(
    (acc, [x, y], i) => acc + (i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`),
    ''
  ) + ' Z';
}

function polylinePath(points) {
  if (!points?.length) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
}

function itemBBox(item) {
  switch (item.tool) {
    case 'pen':
    case 'highlighter': {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const [x, y] of item.points || []) {
        if (x < minX) minX = x; if (y < minY) minY = y;
        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
      }
      const pad = (item.size || 4) / 2;
      return { x: minX - pad, y: minY - pad, w: (maxX - minX) + pad * 2, h: (maxY - minY) + pad * 2 };
    }
    case 'rect':
      return { x: Math.min(item.x, item.x + item.w), y: Math.min(item.y, item.y + item.h), w: Math.abs(item.w), h: Math.abs(item.h) };
    case 'line':
      return {
        x: Math.min(item.x1, item.x2), y: Math.min(item.y1, item.y2),
        w: Math.abs(item.x2 - item.x1), h: Math.abs(item.y2 - item.y1),
      };
    case 'text': {
      const fs = item.fontSize || 16;
      const w = Math.max(20, (item.text || '').length * fs * 0.55);
      return { x: item.x, y: item.y, w, h: fs * 1.3 };
    }
    default:
      return { x: 0, y: 0, w: 0, h: 0 };
  }
}

function pointInPolygon(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect = ((yi > pt[1]) !== (yj > pt[1])) &&
      (pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi || 1e-9) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function bboxIntersectsPolygon(bbox, poly) {
  const corners = [
    [bbox.x, bbox.y], [bbox.x + bbox.w, bbox.y],
    [bbox.x, bbox.y + bbox.h], [bbox.x + bbox.w, bbox.y + bbox.h],
    [bbox.x + bbox.w / 2, bbox.y + bbox.h / 2],
  ];
  for (const c of corners) if (pointInPolygon(c, poly)) return true;
  for (const v of poly) {
    if (v[0] >= bbox.x && v[0] <= bbox.x + bbox.w && v[1] >= bbox.y && v[1] <= bbox.y + bbox.h) return true;
  }
  return false;
}

function pointInBBox(pt, bbox) {
  return pt[0] >= bbox.x - SELECT_HANDLE_PAD && pt[0] <= bbox.x + bbox.w + SELECT_HANDLE_PAD
      && pt[1] >= bbox.y - SELECT_HANDLE_PAD && pt[1] <= bbox.y + bbox.h + SELECT_HANDLE_PAD;
}

function translateItem(item, dx, dy) {
  switch (item.tool) {
    case 'pen':
    case 'highlighter':
      return {
        ...item,
        points: item.points.map((pt) => pt.length > 2 ? [pt[0] + dx, pt[1] + dy, pt[2]] : [pt[0] + dx, pt[1] + dy]),
      };
    case 'rect':
    case 'text':
      return { ...item, x: item.x + dx, y: item.y + dy };
    case 'line':
      return { ...item, x1: item.x1 + dx, y1: item.y1 + dy, x2: item.x2 + dx, y2: item.y2 + dy };
    default:
      return item;
  }
}

// ────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────
export default function AnnotationCanvas({
  pageWidth, pageHeight, strokes, onChange, tool, color, size, disabled,
}) {
  const items = strokes?.strokes || [];
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const svgRef = useRef(null);
  const historyRef = useRef({ past: [], future: [] });
  const [, forceRender] = useState(0); // for floating button enabled-state

  const [drawing, setDrawing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dragOrigin, setDragOrigin] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // ── Coord conversion ────────────────────────────────────────────────────
  const toPdfCoords = useCallback((evt) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((evt.clientX - rect.left) / rect.width) * pageWidth;
    const y = ((evt.clientY - rect.top) / rect.height) * pageHeight;
    return [x, y, evt.pressure || 0.5];
  }, [pageWidth, pageHeight]);

  // ── History ────────────────────────────────────────────────────────────
  const pushHistory = useCallback((prevItems) => {
    historyRef.current.past.push(prevItems);
    if (historyRef.current.past.length > HISTORY_LIMIT) historyRef.current.past.shift();
    historyRef.current.future = [];
    forceRender((n) => n + 1);
  }, []);

  const commit = useCallback((nextItems) => {
    pushHistory(itemsRef.current);
    onChange({ strokes: nextItems });
  }, [onChange, pushHistory]);

  const undo = useCallback(() => {
    const past = historyRef.current.past;
    if (!past.length) return;
    const prev = past.pop();
    historyRef.current.future.push(itemsRef.current);
    onChange({ strokes: prev });
    setSelectedIds(new Set());
    setEditingId(null);
    forceRender((n) => n + 1);
  }, [onChange]);

  const redo = useCallback(() => {
    const future = historyRef.current.future;
    if (!future.length) return;
    const next = future.pop();
    historyRef.current.past.push(itemsRef.current);
    onChange({ strokes: next });
    setSelectedIds(new Set());
    setEditingId(null);
    forceRender((n) => n + 1);
  }, [onChange]);

  const deleteSelection = useCallback(() => {
    if (!selectedIds.size) return;
    commit(itemsRef.current.filter((i) => !selectedIds.has(i.id)));
    setSelectedIds(new Set());
  }, [selectedIds, commit]);

  // Backspace/Delete on selection. Ignore when typing.
  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedIds.size) {
        e.preventDefault();
        deleteSelection();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, selectedIds, deleteSelection]);

  // ── Eraser hit ─────────────────────────────────────────────────────────
  const eraseAt = useCallback((point) => {
    const eraseR = 12;
    const next = itemsRef.current.filter((s) => {
      switch (s.tool) {
        case 'pen':
        case 'highlighter': {
          for (const [px, py] of s.points || []) {
            const dx = px - point[0], dy = py - point[1];
            if (dx * dx + dy * dy < eraseR * eraseR) return false;
          }
          return true;
        }
        default:
          return !pointInBBox(point, itemBBox(s));
      }
    });
    if (next.length === itemsRef.current.length) return;
    onChange({ strokes: next });
  }, [onChange]);

  // ── Pointer dispatch ───────────────────────────────────────────────────
  const onPointerDown = (evt) => {
    if (disabled) return;
    if (editingId) return;
    if (evt.pointerType === 'mouse' && evt.button !== 0) return;
    evt.preventDefault();
    svgRef.current.setPointerCapture(evt.pointerId);
    const point = toPdfCoords(evt);

    if (tool === 'pen' || tool === 'highlighter') {
      setDrawing({
        kind: 'stroke',
        item: {
          id: crypto.randomUUID(), tool, color,
          size: tool === 'highlighter' ? size * 4 : size,
          points: [point],
        },
      });
      return;
    }
    if (tool === 'eraser') {
      // One history snapshot per erase pass
      pushHistory(itemsRef.current);
      eraseAt(point);
      setDrawing({ kind: 'erase' });
      return;
    }
    if (tool === 'rect') {
      setDrawing({ kind: 'rect', item: { id: crypto.randomUUID(), tool: 'rect', color, size, x: point[0], y: point[1], w: 0, h: 0 } });
      return;
    }
    if (tool === 'line') {
      setDrawing({ kind: 'line', item: { id: crypto.randomUUID(), tool: 'line', color, size, x1: point[0], y1: point[1], x2: point[0], y2: point[1] } });
      return;
    }
    if (tool === 'text') {
      const fontSize = Math.max(12, size * 4);
      const id = crypto.randomUUID();
      const newItem = { id, tool: 'text', color, fontSize, x: point[0], y: point[1], text: '' };
      commit([...itemsRef.current, newItem]);
      setEditingId(id);
      setSelectedIds(new Set([id]));
      return;
    }
    if (tool === 'select') {
      const hit = [...itemsRef.current].reverse().find((i) => pointInBBox(point, itemBBox(i)));
      if (hit) {
        const nextSel = selectedIds.has(hit.id) ? selectedIds : new Set([hit.id]);
        if (nextSel !== selectedIds) setSelectedIds(nextSel);
        const originalItems = itemsRef.current.map((it) => ({ ...it }));
        setDragOrigin({ startPdf: point, originalItems, selectionAtStart: nextSel });
        setDrawing({ kind: 'move' });
      } else {
        setDrawing({ kind: 'lasso', polygon: [point] });
        setSelectedIds(new Set());
      }
      return;
    }
  };

  const onPointerMove = (evt) => {
    if (!drawing) return;
    const point = toPdfCoords(evt);

    if (drawing.kind === 'stroke') {
      setDrawing((prev) => ({ ...prev, item: { ...prev.item, points: [...prev.item.points, point] } }));
      return;
    }
    if (drawing.kind === 'erase') { eraseAt(point); return; }
    if (drawing.kind === 'rect') {
      setDrawing((prev) => ({ ...prev, item: { ...prev.item, w: point[0] - prev.item.x, h: point[1] - prev.item.y } }));
      return;
    }
    if (drawing.kind === 'line') {
      setDrawing((prev) => ({ ...prev, item: { ...prev.item, x2: point[0], y2: point[1] } }));
      return;
    }
    if (drawing.kind === 'lasso') {
      setDrawing((prev) => ({ ...prev, polygon: [...prev.polygon, point] }));
      return;
    }
    if (drawing.kind === 'move' && dragOrigin) {
      const dx = point[0] - dragOrigin.startPdf[0];
      const dy = point[1] - dragOrigin.startPdf[1];
      const sel = dragOrigin.selectionAtStart;
      const next = dragOrigin.originalItems.map((it) => sel.has(it.id) ? translateItem(it, dx, dy) : it);
      onChange({ strokes: next });
      return;
    }
  };

  const onPointerUp = (evt) => {
    if (!drawing) return;
    const finalDrawing = drawing;
    setDrawing(null);

    if (finalDrawing.kind === 'stroke') {
      if (finalDrawing.item.points.length < 2) return;
      commit([...itemsRef.current, finalDrawing.item]);
      return;
    }
    if (finalDrawing.kind === 'rect') {
      const item = { ...finalDrawing.item };
      if (Math.abs(item.w) < 4 || Math.abs(item.h) < 4) return;
      if (item.w < 0) { item.x += item.w; item.w = -item.w; }
      if (item.h < 0) { item.y += item.h; item.h = -item.h; }
      commit([...itemsRef.current, item]);
      return;
    }
    if (finalDrawing.kind === 'line') {
      const { x1, y1, x2, y2 } = finalDrawing.item;
      if (Math.hypot(x2 - x1, y2 - y1) < 4) return;
      commit([...itemsRef.current, finalDrawing.item]);
      return;
    }
    if (finalDrawing.kind === 'lasso') {
      const poly = finalDrawing.polygon;
      if (poly.length < 3) return;
      const sel = new Set();
      for (const it of itemsRef.current) {
        if (bboxIntersectsPolygon(itemBBox(it), poly)) sel.add(it.id);
      }
      setSelectedIds(sel);
      return;
    }
    if (finalDrawing.kind === 'move' && dragOrigin) {
      const point = toPdfCoords(evt);
      const dx = point[0] - dragOrigin.startPdf[0];
      const dy = point[1] - dragOrigin.startPdf[1];
      const origin = dragOrigin;
      setDragOrigin(null);
      if (Math.hypot(dx, dy) < 1) return;
      pushHistory(origin.originalItems);
      return;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  const cursorFor = () => {
    if (disabled) return 'default';
    if (tool === 'select') return 'default';
    if (tool === 'text') return 'text';
    return 'crosshair';
  };

  const renderItem = (item) => {
    const isSelected = selectedIds.has(item.id);
    let body = null;
    switch (item.tool) {
      case 'highlighter':
        body = <path d={polylinePath(item.points)} stroke={item.color} strokeWidth={item.size}
          strokeLinecap="round" strokeLinejoin="round" fill="none" opacity={HIGHLIGHTER_OPACITY} />;
        break;
      case 'pen':
        body = <path d={penPath(item)} fill={item.color} stroke="none" />;
        break;
      case 'rect':
        body = <rect x={item.x} y={item.y} width={item.w} height={item.h}
          stroke={item.color} strokeWidth={item.size} fill="none" />;
        break;
      case 'line':
        body = <line x1={item.x1} y1={item.y1} x2={item.x2} y2={item.y2}
          stroke={item.color} strokeWidth={item.size} strokeLinecap="round" />;
        break;
      case 'text':
        if (item.id === editingId) {
          body = (
            <foreignObject x={item.x} y={item.y}
              width={Math.max(160, (item.text || '').length * (item.fontSize || 16) * 0.6 + 60)}
              height={(item.fontSize || 16) * 1.6}>
              <input
                autoFocus
                defaultValue={item.text}
                onBlur={(e) => {
                  const text = e.target.value;
                  setEditingId(null);
                  if (!text.trim()) {
                    commit(itemsRef.current.filter((i) => i.id !== item.id));
                  } else if (text !== item.text) {
                    commit(itemsRef.current.map((i) => (i.id === item.id ? { ...i, text } : i)));
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') e.target.blur(); }}
                style={{
                  width: '100%', font: `${item.fontSize}px sans-serif`, color: item.color,
                  background: 'rgba(255,255,255,0.85)', border: '1px dashed #2563eb',
                  outline: 'none', padding: '2px 4px', boxSizing: 'border-box',
                }}
              />
            </foreignObject>
          );
        } else {
          body = (
            <text x={item.x + 4} y={item.y + (item.fontSize || 16)}
              fill={item.color} fontSize={item.fontSize || 16} fontFamily="sans-serif"
              onDoubleClick={() => { if (!disabled) setEditingId(item.id); }}>
              {item.text}
            </text>
          );
        }
        break;
      default: body = null;
    }
    let overlay = null;
    if (isSelected) {
      const bb = itemBBox(item);
      overlay = <rect x={bb.x - 2} y={bb.y - 2} width={bb.w + 4} height={bb.h + 4}
        fill="none" stroke="#2563eb" strokeWidth={1} strokeDasharray="4 3" pointerEvents="none" />;
    }
    return <React.Fragment key={item.id}>{body}{overlay}</React.Fragment>;
  };

  const liveDrawing = (() => {
    if (!drawing) return null;
    const { kind, item, polygon } = drawing;
    if (kind === 'stroke' && item.points.length >= 2) {
      return item.tool === 'highlighter' ? (
        <path d={polylinePath(item.points)} stroke={item.color} strokeWidth={item.size}
          strokeLinecap="round" strokeLinejoin="round" fill="none" opacity={HIGHLIGHTER_OPACITY} />
      ) : (
        <path d={penPath(item)} fill={item.color} stroke="none" />
      );
    }
    if (kind === 'rect') {
      const x = Math.min(item.x, item.x + item.w), y = Math.min(item.y, item.y + item.h);
      return <rect x={x} y={y} width={Math.abs(item.w)} height={Math.abs(item.h)}
        stroke={item.color} strokeWidth={item.size} fill="none" strokeDasharray="3 2" />;
    }
    if (kind === 'line') {
      return <line x1={item.x1} y1={item.y1} x2={item.x2} y2={item.y2}
        stroke={item.color} strokeWidth={item.size} strokeLinecap="round" strokeDasharray="3 2" />;
    }
    if (kind === 'lasso' && polygon?.length) {
      return <path d={polylinePath(polygon) + ' Z'} fill="rgba(37,99,235,0.08)"
        stroke="#2563eb" strokeWidth={1} strokeDasharray="4 3" />;
    }
    return null;
  })();

  const undoCount = historyRef.current.past.length;
  const redoCount = historyRef.current.future.length;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${pageWidth} ${pageHeight}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        touchAction: 'none', cursor: cursorFor(),
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {items.map(renderItem)}
      {liveDrawing}

      {!disabled && (
        <g transform={`translate(${pageWidth - 110}, 8)`}>
          <FloatingButton x={0} label="↶" disabled={!undoCount} onClick={undo} />
          <FloatingButton x={32} label="↷" disabled={!redoCount} onClick={redo} />
          {selectedIds.size > 0 && <FloatingButton x={64} label="✕" danger onClick={deleteSelection} />}
        </g>
      )}
    </svg>
  );
}

function FloatingButton({ x, label, onClick, disabled, danger }) {
  return (
    <g transform={`translate(${x}, 0)`} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
       onPointerDown={(e) => { e.stopPropagation(); }}
       onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}>
      <rect width={28} height={28} rx={6} ry={6}
        fill={disabled ? 'rgba(255,255,255,0.6)' : (danger ? '#fee2e2' : 'rgba(255,255,255,0.95)')}
        stroke={danger ? '#fca5a5' : '#cbd5e1'} strokeWidth={1} />
      <text x={14} y={20} textAnchor="middle" fontSize={16} fontFamily="sans-serif"
        fill={disabled ? '#cbd5e1' : (danger ? '#b91c1c' : '#0f172a')}>{label}</text>
    </g>
  );
}
